import react, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { setUser } from '@/store/slices/userSlice';
import { CallAPI } from '@/services/utils'
import Loader from '@/components/Loader';
import {
    exchangeEmbedToken,
    getLoginRedirectUrl,
    notifyParentAuthRequired,
    takeEmbedTokenFromUrl,
} from '@/services/auth';
import { getAccessToken } from '@/services/tokenStorage';
import { isEmbeddedApp } from '@/utils/embedMode';

const AuthWrapper = ({ children, forRole }) => {
    const [loading, setLoading] = useState(true);
    const [waitingForParent, setWaitingForParent] = useState(false);
    const dispatch = useDispatch();

    const { user } = useSelector(state => state.userDetails);
    const { message, role, permission = {} } = user || {}

    const router = useRouter();

    const isLoggedIn = () => {
        if (message === 'success' && user != null) {
            return true
        } else {
            return false
        }
    }

    const isAdmin = role === 'admin';
    const appPermission = permission['safeHavenScorecard_view'];

    const adminRoutes = ['/admin']

    const isAdminRoute = adminRoutes.some(route => router.asPath.includes(route));
    const loginRedirectUrl = getLoginRedirectUrl();

    const handleRedirection = () => {
        if (isEmbeddedApp() && notifyParentAuthRequired()) {
            setWaitingForParent(true);
            return;
        }
        router.push(loginRedirectUrl);
    };

    const verifyUser = async () => {
        try {
            const urlEmbedToken = takeEmbedTokenFromUrl();
            let exchanged = null;
            if (urlEmbedToken) {
                exchanged = await exchangeEmbedToken(urlEmbedToken);
                if (exchanged && exchanged.message === 'success') {
                    dispatch(setUser(exchanged));
                }
            }

            const verify = await CallAPI(
                '/user/verify-user',
                'GET',
                {},
            )

            if (verify && verify.message === 'success') {
                dispatch(setUser(verify));
                return;
            }

            if (exchanged && exchanged.message === 'success' && getAccessToken()) {
                return;
            }

            handleRedirection();
        } catch (error) {
            if (!getAccessToken()) {
                handleRedirection();
            }
        }
    }

    useEffect(() => {
        if (user == null) {
            setLoading(true);
            verifyUser().then(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [])

    useEffect(() => {
        if (loading || waitingForParent) {
            return;
        }
        if (!isLoggedIn()) {
            handleRedirection();
            return
        }

        if (isLoggedIn() && !isAdmin && isAdminRoute) {
            router.replace('/unauthorized');
            return
        }
    }, [user, loading, waitingForParent]);

    useEffect(() => {
        if (isLoggedIn() && !appPermission) {
            router.replace('/unauthorized');
            return
        }
    }, [user, loading]);

    if (loading || waitingForParent) {
        return (
            <>
                <Loader loading={true} />
            </>
        )
    }

    if (!isLoggedIn()) {
        return <></>
    }

    return (<>{children}</>)
};

export default AuthWrapper;
